import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios from '@axios';
import { RootState } from '@redux/reducers';
import '@extensions/string';
import { ThunkCallback } from '../model/thunk-callback';
import { addNotification } from '../notification';
import { translate } from '@components/i18n';
import { PaginatedResponseApi } from '@models/order/items/response';
import { AuthorizedClientRequest, AuthorizedClientResponse, AuthorizedClientsParams } from '@models/authorized-clients';
import { exportReport } from '@utils/file';

export const fetchAuthorizedClients = createAsyncThunk<
    PaginatedResponseApi<AuthorizedClientResponse>, AuthorizedClientsParams | void
>('authorized-clients/list', async (req, thunkAPI) => {
    try {
        const response = await axios.get<PaginatedResponseApi<AuthorizedClientResponse>>(
            `/clienteaprovacao`,  { params: req },
        );

        const { data, status } = response;

        if (!data) {
            return {
                content: [],
                number: 0,
                totalElements: 0,
                size: 0,
                totalPages: 0,
                numberOfElements: 0,
                pageable: null,
                last: true,
                first: true,
                sort: null,
                empty: true,
            };
        } else {
            if (status === 200) {
                return data;
            }
            throw new Error();
        }
    } catch (e) {
        // TODO: here will be fetch's actions
        return thunkAPI.rejectWithValue(translate('general.erroListingItems'));
    }
});

export const createAuthorizedClient = createAsyncThunk<
    AuthorizedClientResponse,
    ThunkCallback<AuthorizedClientRequest>
>('authorized-clients/create', async (request, thunkAPI) => {
    try {
        const response = await axios.post<AuthorizedClientResponse>('/clienteaprovacao', request.data);

        const { data, status } = response;

        if (status === 201 && data) {
            thunkAPI.dispatch(
                addNotification({
                    type: 'success',
                    message: translate('general.updated'),
                    title: translate('general.success'),
                    notificationKey: request.notificationKey,
                    callback: request.onSuccess,
                }),
            );

            return data;
        } else {
            return thunkAPI.rejectWithValue(data);
        }
    } catch (e) {
      if(e.response.data && e.response.data.length > 0){
          e.response.data.forEach((error, index) => {
              thunkAPI.dispatch(
                  addNotification({
                      type: 'error',
                      message: error.msgUsuario,
                      title: translate('general.errorT'),
                      notificationKey: index === 0 ? request.notificationKey : null
                  }),
              );
          })
      }else{
          thunkAPI.dispatch(
              addNotification({
                  type: 'error',
                  message: translate('general.error'),
                  title: translate('general.errorT'),
                  notificationKey: request.notificationKey,
              }),
          );
      }
      return thunkAPI.rejectWithValue(e.message);
    }
});

export const removeAuthorizedClient = createAsyncThunk<void, ThunkCallback<number>>(
    'authorized-clients/disable',
    async (req, thunkAPI) => {
        try {
          thunkAPI.dispatch(
            addNotification({
              type: 'loading',
              message: translate('general.deleting'),
              title: translate('general.deleting'),
              notificationKey: req.notificationKey
           }))
          const response = await axios.put('/clienteaprovacao/desabilitar/' + req.data);
          if (response.status === 200) {
            thunkAPI.dispatch(removeItemState(req.data));
          }
          thunkAPI.dispatch(addNotification({
              type: 'success',
              message: translate('general.success'),
              title: translate('general.deleteSuccess'),
              notificationKey: req.notificationKey,
              callback: req.onSuccess,
          }))
        } catch (e) {
          if(e.response.data && e.response.data.length > 0){
              e.response.data.forEach((error, index) => {
                  thunkAPI.dispatch(
                      addNotification({
                          type: 'error',
                          message: error.msgUsuario,
                          title: translate('general.errorT'),
                          notificationKey: index === 0 ? req.notificationKey : null
                      }),
                  );
              })
          }else{
              thunkAPI.dispatch(
                  addNotification({
                      type: 'error',
                      message: translate('general.error'),
                      title: translate('general.errorT'),
                      notificationKey: req.notificationKey,
                  }),
              );
          }
          return thunkAPI.rejectWithValue(e.message);
        }
    },
);

export const exportAuthorizedClients = createAsyncThunk<void, void>('authorized-clients/export', async (request, thunkAPI) => {
    try {
        const response = await axios.get(`/clienteaprovacao/export`, { params: request, responseType: 'blob' });

        const { data, status } = response;

        if (status === 200) {
            exportReport(data,`${translate('general.exportAuthorizedClients')}.xlsx`);
        } else {
            return thunkAPI.rejectWithValue('');
        }
    } catch (e) {
        return thunkAPI.rejectWithValue(translate('general.erroListingItems'));
    }
});

interface IUItemsState {
    response?: PaginatedResponseApi<AuthorizedClientResponse>;
    isFetching: boolean;
    isSuccess: boolean;
    isError: boolean;
    errorMessage?: string;
}

const initialState: IUItemsState = {
    isFetching: false,
    isSuccess: false,
    isError: false,
    errorMessage: '',
};

const authorizedClientsSlice = createSlice({
    name: 'authorizedClientsSlice',
    initialState,
    reducers: {
        clearState: (state) => {
            state = { ...initialState };
            return state;
        },
        removeItemState: (state, action: PayloadAction<number>) => {
            state.response.content = state.response.content.filter((u) => u.id !== action.payload);
            return state;
        },
    },
    extraReducers: {
        [fetchAuthorizedClients.pending.toString()]: (state) => {
            state.isFetching = true;
            return state;
        },
        [fetchAuthorizedClients.rejected.toString()]: (state, action) => {
            state.isFetching = false;
            state.isError = true;
            state.errorMessage = action.payload;
            return state;
        },
        [fetchAuthorizedClients.fulfilled.toString()]: (
            state,
            { payload }: PayloadAction<PaginatedResponseApi<AuthorizedClientResponse>>,
        ) => {
            state.isFetching = false;
            state.isSuccess = true;
            state.response = payload;
            return state;
        },
    },
});

export const authorizedClientSelector = (state: RootState): IUItemsState => state.authorizedClients;

export const allAuthorizedClients = ({ authorizedClients }: RootState): AuthorizedClientResponse[] => authorizedClients.response?.content || [];

export const { clearState, removeItemState } = authorizedClientsSlice.actions;

export default authorizedClientsSlice.reducer;
